import {
  MedigapFilterItem,
  MedigapPlanName, MedigapQuoteQuery,
  useMedigapFiltersQuery
} from '@coverright/data-access/types/medigap';
import { useIsMAState, useIsWIState } from '@coverright/data-access/medicare';
import omit from 'lodash/omit';
import flatMap from 'lodash/flatMap';
import { planCategories, PlanCategory } from './plan-categories';

export interface ListItem {
  value: string;
  label: string;
  disabled: boolean;
  count?: number;
  // Companies only (plan names have none). A single company maps to many NAICs - Anthem has 13 -
  // which the underwriting questionnaire needs, since its question sets are keyed per NAIC.
  naics?: string[];
}

export interface MgPlanFilter {
  planNames: ListItem[];
  companies: ListItem[];
}

export function useMgPlanFilter(
  quote: MedigapQuoteQuery['medigapQuote'],
  planNames: string[] = [],
  companies: string[] = []
): MgPlanFilter {
  const isMassachusetts = useIsMAState(quote?.medigapFilterState.zip);
  const isWisconsin = useIsWIState(quote?.medigapFilterState.zip);

  const { data, previousData } = useMedigapFiltersQuery({
    variables: {
      filter: {
        ...omit(quote?.medigapFilterState, [
          'planCategory',
          '__typename',
          'id',
        ]),
        companies,
        monthlyPlanPremiumRanges:
          quote?.medigapFilterState.monthlyPlanPremiumRanges || [],
        planNames:
          isWisconsin || isMassachusetts
            ? []
            : (planNames as MedigapPlanName[]),
      },
    },
    skip: !quote,
  });

  const result = data || previousData;

  const allValuedPlanNames = flatMap(planCategories, 'plans');

  let medigapPlanNames =
    result?.medigapPlanNames.filter((plan) =>
      allValuedPlanNames.includes(plan.name as MedigapPlanName)
    ) || [];

  const planCategory =
    quote?.medigapFilterState.planCategory || 'all';

  if (flatMap(planCategories, 'name').includes(planCategory)) {
    const categoryPlanNames = planCategories.find(
      (catName) => catName.name === planCategory
    )?.plans;
    if (categoryPlanNames) {
      medigapPlanNames = medigapPlanNames.filter((plan) =>
        categoryPlanNames.includes(plan.name as MedigapPlanName)
      );
    }
  }

  return {
    planNames: transformListItem(medigapPlanNames),
    companies: transformListItem(result?.medigapCompanies || []),
  };
}

function transformListItem(
  collection: Omit<MedigapFilterItem, '__typename' | 'count'>[]
): ListItem[] {
  return collection.map((item: any) => ({
    value: item.key,
    label: item.name,
    disabled: item.count === 0,
    naics: item.naics,
  }));
}
